/* mm-cart: behaviour for the Maison cart. Loaded (deferred) by snippets/mm-bundle-pack-assets from
   sections/mm-cart-drawer (every page) and sections/mm-main-cart. Needs window.MM (assets/mm-global.js).

   <mm-cart-drawer>  right-side drawer. Opens on clicks of [data-mm-cart-open] (header cart icon) when Theme settings ›
                     Cart type is Drawer, and on `mm:cart:updated` with detail.open (except on the cart page). Closes on
                     scrim, Escape and the close button. Re-renders its content through the Section Rendering API on
                     every `mm:cart:updated`, keeping open state, scroll position and focus. Focus trap while open,
                     focus returns to the trigger, page scroll locked. Shopify's express checkout buttons keep their
                     own buttons in shadow DOM: forward Tab walks through them and lands on the [data-mm-trap-end]
                     sentinel at the end of the panel, which sends focus back to the top.
   <mm-cart-page>    the cart page: same line buttons, same re-render on `mm:cart:updated`.
   <mm-cart-rewards> free-shipping / free-gift progress (port of live assets/mm-rewards.js).
   <mm-cart-nudge>   "Add another for $20.00 and save $9.95, plus free shipping" on a bundle-tier product's line (R2, wording
                     R3): priced with MaisonEngine.tierLocalCents; its button is the line's own "+" (below), nothing new is sent.
   <mm-cart-upsells> suggestions: the free-shipping lead for currencies whose minimum is converted here (R2).
   <mm-cart-goal>    the empty drawer's free-shipping threshold line, for those same currencies (R2).

   Line buttons (snippets/mm-cart-line, mm-bundle-line, mm-bundle-merged). Requests are live's, sent through MM so every
   change fires `mm:cart:updated`:
     [data-mm-change="<line key>"][data-qty="n"]  MM.changeLine(key, n)      POST cart/change.js { id, quantity }
                                                  (the "Add another" nudge carries the line's own "+" request)
     [data-mm-pack='{"<key>":n,…}']               MM.updateLines(updates)    POST cart/update.js { updates }
     [data-mm-pack-add='[items]']                 MM.addItems(items)         POST cart/add.js { items }, each item's
                                                  _mm_bundle given one new group id (live: "+" on a merged bundle adds
                                                  another bundle as its own group, so each stays one merged line)
     form[data-mm-gift-form]                      MM.addItems([{ id, quantity: 1, properties: { _mm_gift: '1' } }])
   Nothing else is sent: no new properties, no cart attributes. */
(() => {
  if (window.MMCart) return;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const root = () => window.Shopify?.routes?.root || '/';
  const cartType = () => document.documentElement.dataset.mmCartType || 'drawer';
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  // Header cart icons. [aria-controls="cart-drawer"] is Horizon's header cart button, bridged until mm-header ships.
  const TRIGGERS = '[data-mm-cart-open], [aria-controls="cart-drawer"]';

  /* ---------- Section Rendering API ---------- */
  // No "Accept: application/json" header: on /cart that makes Shopify answer with the cart JSON (= /cart.js) instead of
  // the rendered sections.
  async function fetchSection(url, id, signal) {
    const res = await fetch(url, { cache: 'no-store', signal });
    if (!res.ok) throw new Error(`Section ${id} responded ${res.status}`);
    const html = (await res.json())[id];
    if (!html) throw new Error(`Section ${id} missing from the response`);
    return new DOMParser().parseFromString(html, 'text/html');
  }

  // Replace host's [data-mm-cart-content] with the freshly rendered one. Scroll positions of [data-mm-cart-scroll] and
  // the focused control (matched by data-mm-focus) carry over; if that control is gone, focus moves to the fallback.
  function swap(host, doc, fallback) {
    const cur = host.querySelector('[data-mm-cart-content]');
    const next = doc.querySelector('[data-mm-cart-content]');
    if (!cur || !next) return false;
    const scrolls = [...cur.querySelectorAll('[data-mm-cart-scroll]')].map((el) => el.scrollTop);
    const active = document.activeElement;
    const hadFocus = !!active && cur.contains(active);
    const focusKey = hadFocus ? active.closest('[data-mm-focus]')?.dataset.mmFocus : null;
    cur.replaceWith(next);
    next.querySelectorAll('[data-mm-cart-scroll]').forEach((el, i) => { if (scrolls[i]) el.scrollTop = scrolls[i]; });
    if (hadFocus) {
      const same = focusKey && [...next.querySelectorAll('[data-mm-focus]')].find((el) => el.dataset.mmFocus === focusKey && !el.disabled);
      (same || fallback())?.focus({ preventScroll: true });
    }
    return true;
  }

  // One renderer per host: the newest request wins, older ones are aborted. host.pending settles (never rejects)
  // once the content on screen matches the cart.
  function makeRenderer(host, urlFor, fallback) {
    let seq = 0;
    let ctrl;
    host.pending = Promise.resolve();
    return () => {
      const mine = ++seq;
      ctrl?.abort();
      ctrl = new AbortController();
      const id = host.dataset.sectionId;
      host.querySelector('[data-mm-cart-content]')?.setAttribute('aria-busy', 'true');
      host.pending = fetchSection(urlFor(id), id, ctrl.signal)
        .then((doc) => { if (mine === seq) swap(host, doc, fallback); })
        .catch((e) => { if (e?.name !== 'AbortError') console.warn('[mm-cart]', e); })
        .finally(() => { if (mine === seq) host.querySelector('[data-mm-cart-content]')?.removeAttribute('aria-busy'); });
      return host.pending;
    };
  }

  /* ---------- Line buttons ---------- */
  const groupId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6); // same as the Trio builder
  const freshGroup = (items) => {
    const key = String(items[0]?.properties?._mm_bundle || 'trio:x').split(':')[0];
    const tag = `${key}:${groupId()}`;
    return items.map((i) => ({ ...i, properties: { ...i.properties, _mm_bundle: tag } }));
  };
  const failText = (e) => e?.message || 'Could not update your cart. Please try again.';

  async function lineAction(btn, host) {
    const line = btn.closest('[data-mm-line]');
    if (btn.disabled || line?.getAttribute('aria-busy') === 'true') return;
    const err = line?.querySelector('[data-mm-line-error]');
    line?.setAttribute('aria-busy', 'true');
    if (err) { err.hidden = true; err.textContent = ''; }
    try {
      const d = btn.dataset;
      if (d.mmChange != null) await window.MM.changeLine(d.mmChange, Number(d.qty));
      else if (d.mmPack) await window.MM.updateLines(JSON.parse(d.mmPack));
      else if (d.mmPackAdd) await window.MM.addItems(freshGroup(JSON.parse(d.mmPackAdd)), { open: false });
      await host.pending;
    } catch (e) {
      if (err) { err.textContent = failText(e); err.hidden = false; }
    } finally {
      if (line?.isConnected) line.removeAttribute('aria-busy');
    }
  }

  async function giftAdd(form, host) {
    const btn = form.querySelector('[type="submit"]');
    if (btn?.getAttribute('aria-busy') === 'true') return;
    const err = form.closest('[data-mm-gift]')?.querySelector('[data-mm-gift-error]');
    btn?.setAttribute('aria-busy', 'true');
    if (err) err.hidden = true;
    try {
      const fd = new FormData(form);
      await window.MM.addItems([{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1), properties: { _mm_gift: String(fd.get('properties[_mm_gift]') || '1') } }], { open: false });
      await host.pending;
    } catch (e) {
      if (err) { err.textContent = failText(e); err.hidden = false; }
    } finally {
      if (btn?.isConnected) btn.removeAttribute('aria-busy');
    }
  }

  function bindLines(host) {
    host.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-mm-change], [data-mm-pack], [data-mm-pack-add]');
      if (!btn || !host.contains(btn)) return;
      e.preventDefault();
      lineAction(btn, host);
    });
    host.addEventListener('submit', (e) => {
      const gift = e.target.closest('[data-mm-gift-form]');
      if (gift) { e.preventDefault(); giftAdd(gift, host); return; }
      // Checkout: native form post to /cart (checkout); just show the button as busy while the browser navigates.
      if (e.target.closest('[data-mm-checkout]')) e.submitter?.setAttribute('aria-busy', 'true');
    });
    // Back/forward cache: never come back to a spinning checkout button.
    window.addEventListener('pageshow', () => host.querySelectorAll('[data-mm-checkout] [aria-busy]').forEach((b) => b.removeAttribute('aria-busy')));
  }

  /* ---------- Scroll lock (Horizon scrolls .page-wrapper from 990px up; mm-cart.css locks both) ---------- */
  const lockOwners = new Set();
  const syncLock = () => document.documentElement.classList.toggle('mm-cart-locked', [...lockOwners].some((o) => o.isConnected));
  const scrollbarWidth = () => {
    const pw = document.querySelector('.page-wrapper');
    if (pw && /auto|scroll/.test(getComputedStyle(pw).overflowY)) return pw.offsetWidth - pw.clientWidth;
    return window.innerWidth - document.documentElement.clientWidth;
  };
  const lock = (o) => {
    if (!document.documentElement.classList.contains('mm-cart-locked')) document.documentElement.style.setProperty('--mm-sbw', `${Math.max(0, scrollbarWidth())}px`);
    lockOwners.add(o);
    syncLock();
  };
  const unlock = (o) => { lockOwners.delete(o); syncLock(); };
  const syncTriggers = (open) => document.querySelectorAll(TRIGGERS).forEach((t) => t.setAttribute('aria-expanded', String(open)));

  /* ---------- <mm-cart-drawer> ---------- */
  class MmCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-mm-cart-panel]');
      this.status = this.querySelector('[data-mm-cart-status]');
      this.render = makeRenderer(this, (id) => `${root()}?sections=${encodeURIComponent(id)}`, () => this.panel);
      bindLines(this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-mm-cart-close]')) { e.preventDefault(); this.close(); } });
      // Tab past the last control (express checkout buttons included) reaches the sentinel: wrap to the first control.
      this.addEventListener('focusin', (e) => { if (e.target.matches?.('[data-mm-trap-end]')) this.focusables()[0]?.focus(); });
      this.onUpdated = (e) => this.updated(e.detail || {});
      this.onKey = (e) => this.key(e);
      this.onSelect = (e) => { if (e.detail?.sectionId === this.dataset.sectionId) this.open(); };
      this.onDeselect = (e) => { if (e.detail?.sectionId === this.dataset.sectionId) this.close(); };
      document.addEventListener('mm:cart:updated', this.onUpdated);
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
    }

    disconnectedCallback() {
      document.removeEventListener('mm:cart:updated', this.onUpdated);
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
      document.removeEventListener('keydown', this.onKey);
      unlock(this);
    }

    get isOpen() { return this.hasAttribute('open'); }

    updated({ cart, open }) {
      const rendered = this.render();
      if (cart && this.status) this.status.textContent = `Cart updated, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}.`;
      // Open once the new content is in (no flash of the old cart), but never wait more than 700ms for it.
      if (open && !document.querySelector('mm-cart-page')) Promise.race([rendered, new Promise((r) => setTimeout(r, 700))]).then(() => this.open());
    }

    open(trigger) {
      if (cartType() === 'page' && !window.Shopify?.designMode) return;
      if (this.isOpen) return;
      const active = document.activeElement;
      this.returnTo = trigger || (active && active !== document.body ? active : null);
      this.removeAttribute('inert');
      this.querySelector('[data-mm-cart-scroll]')?.scrollTo(0, 0); // newest lines are at the top
      this.setAttribute('open', '');
      lock(this);
      syncTriggers(true);
      document.addEventListener('keydown', this.onKey);
      requestAnimationFrame(() => this.panel?.focus({ preventScroll: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.setAttribute('inert', '');
      unlock(this);
      syncTriggers(false);
      document.removeEventListener('keydown', this.onKey);
      const back = this.returnTo;
      this.returnTo = null;
      (back?.isConnected ? back : document.querySelector('[data-mm-cart-open]'))?.focus({ preventScroll: true });
    }

    focusables() {
      return [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => !el.matches('[data-mm-trap-end]') && el.getClientRects().length && !el.closest('[hidden], [inert]'));
    }

    key(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const items = this.focusables();
      if (!items.length) { e.preventDefault(); this.panel.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const at = document.activeElement;
      const sentinel = this.panel.querySelector('[data-mm-trap-end]');
      if (!this.panel.contains(at)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && (at === first || at === this.panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && at === last && !sentinel) { e.preventDefault(); first.focus(); }
    }
  }

  /* ---------- <mm-cart-page> ---------- */
  class MmCartPage extends HTMLElement {
    connectedCallback() {
      this.render = makeRenderer(this, (id) => `${this.dataset.cartUrl || `${root()}cart`}?sections=${encodeURIComponent(id)}`, () => this.querySelector('[data-mm-cart-focus]'));
      bindLines(this);
      this.onUpdated = () => this.render();
      document.addEventListener('mm:cart:updated', this.onUpdated);
    }
    disconnectedCallback() { document.removeEventListener('mm:cart:updated', this.onUpdated); }
  }

  /* ---------- <mm-cart-rewards> (port of live assets/mm-rewards.js) ---------- */
  const rate = () => Number(window.Shopify?.currency?.rate || 1);
  // Free shipping and the engine's gift tiers (data-round="up5"): USD × rate rounded UP to the next 5, as the engine
  // and the page copy do. Theme-setting gifts without their own amount: live's "clean number" rounding.
  const up5 = (usd) => Math.ceil((Number(usd) * rate()) / 5) * 5 * 100;
  const clean = (v) => { if (!(v > 0)) return 0; const k = 10 ** Math.floor(Math.log10(v)); const d = v / k; const step = Math.max(1, k * (d < 2 ? 0.1 : d < 5 ? 0.25 : 0.5)); return Math.round(v / step) * step; };
  const amountOf = (el) => (el.dataset.amount ? Number(el.dataset.amount) : el.dataset.round === 'up5' ? up5(el.dataset.usd) : clean(Number(el.dataset.usd) * rate()) * 100);
  // Shop money format, like snippets/mm-money: USD keeps its cents, other currencies drop zero cents.
  function formatMoney(cents, format, keepCents) {
    const whole = !keepCents && cents % 100 === 0;
    const num = (precision, thousands = ',', decimal = '.') => {
      const [i, d] = (cents / 100).toFixed(precision).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (d && !whole ? decimal + d : '');
    };
    return String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: num(2), amount_no_decimals: num(0), amount_with_comma_separator: num(2, '.', ','),
      amount_no_decimals_with_comma_separator: num(0, '.', ','), amount_with_apostrophe_separator: num(2, "'", '.'),
      amount_with_period_and_space_separator: num(2, ' ', '.'), amount_with_space_separator: num(2, ' ', ','),
    })[k] ?? num(2));
  }
  const lastFill = { drawer: 0, page: 0 }; // so a re-rendered bar grows from where it was

  class MmCartRewards extends HTMLElement {
    connectedCallback() { this.render(); }
    render() {
      const d = this.dataset;
      const kind = d.context === 'page' ? 'page' : 'drawer';
      const usd = d.currency === 'USD';
      const money = (c, keepCents = false) => formatMoney(c, d.moneyFormat, keepCents); // every currency drops zero cents
      const goalText = (c) => { const s = money(c, false); return s.includes('$') ? `${s} ${d.currency}` : s; };
      const fill = (tpl, html) => esc(tpl).replace('[amount]', html);
      const total = Number(d.total || 0);
      const giftTotal = d.giftTotal ? Number(d.giftTotal) : total;
      const ship = d.ship ? Number(d.ship) : up5(d.shipUsd || 45);
      const stops = [...this.querySelectorAll('[data-mm-stop]')]
        .map((el) => ({ el, kind: el.dataset.mmStop, amt: el.dataset.mmStop === 'ship' ? ship : amountOf(el) }))
        .filter((s) => s.amt > 0)
        .sort((a, b) => a.amt - b.amt);
      const val = (s) => (s.kind === 'ship' ? total : giftTotal);
      // Milestones sit evenly along the bar (first at 20%, last at the end); the fill runs piecewise so it meets each
      // marker exactly when its amount is reached. One milestone (free shipping only) = plain progress.
      const at = stops.length === 1 ? [100] : stops.map((_, i) => 20 + (i * 80) / (stops.length - 1));
      const next = stops.find((s) => val(s) < s.amt);
      stops.forEach((s, i) => {
        s.el.style.setProperty('--at', `${at[i]}%`);
        s.el.classList.toggle('is-reached', val(s) >= s.amt);
        s.el.classList.toggle('is-next', s === next);
        const label = s.el.querySelector('[data-mm-stop-label]');
        if (label) label.innerHTML = `<span>${s.kind === 'ship' ? 'Free shipping' : 'Free gift'}</span><span>${esc(goalText(s.amt))}</span>`;
      });
      let line = 0;
      stops.forEach((s, i) => {
        const prevAmt = i ? stops[i - 1].amt : 0;
        const prevAt = i ? at[i - 1] : 0;
        if (val(s) >= s.amt) line = at[i];
        else if (val(s) > prevAmt && line === prevAt) line = prevAt + ((val(s) - prevAmt) / (s.amt - prevAmt)) * (at[i] - prevAt);
      });
      // Gift rows: shown once unlocked; a gift already in the cart whose tier was lost shows a warning instead.
      this.querySelectorAll('[data-mm-gift]').forEach((g) => {
        const amt = amountOf(g);
        const keep = g.dataset.inCart === 'true' && giftTotal < amt;
        g.hidden = giftTotal < amt && !keep;
        g.classList.toggle('is-at-risk', keep);
        const warn = g.querySelector('[data-mm-warn]');
        if (warn) { warn.hidden = !keep; if (keep) warn.innerHTML = fill(g.dataset.warn || '', `<strong>${esc(money(amt - giftTotal))}</strong>`); }
      });
      const msg = this.querySelector('[data-mm-rw-msg]');
      if (msg) {
        msg.innerHTML = next
          ? fill(next.kind === 'ship' ? d.tAway : d.tGiftAway, `<strong>${esc(money(next.amt - val(next)))}</strong>`)
          : esc(stops.some((s) => s.kind === 'gift') ? d.tAllDone : d.tDone);
      }
      const goal = this.querySelector('[data-mm-rw-goal]');
      if (goal) goal.innerHTML = fill(d.tLabel || '', esc(goalText(ship)));
      const zero = this.querySelector('[data-mm-rw-zero]');
      if (zero) zero.textContent = money(0, false);
      this.classList.toggle('is-complete', !next);
      const pct = Math.max(0, Math.min(100, line));
      this.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', String(Math.round(pct)));
      const bar = this.querySelector('[data-mm-rw-fill]');
      if (!bar) return;
      const from = lastFill[kind];
      lastFill[kind] = pct;
      if (reduceMotion() || from === pct) { bar.style.width = `${pct}%`; return; }
      bar.style.transition = 'none';
      bar.style.width = `${from}%`;
      bar.getBoundingClientRect();
      bar.style.transition = '';
      bar.style.width = `${pct}%`;
    }
  }

  /* ---------- Free-shipping maths shared by the R2 lines below (same numbers as the bar) ---------- */
  // Minimum in cart cents: the currency's own amount (data-ship), else the USD amount converted, rounded up to the next 5.
  const shipOf = (d) => (d.ship ? Number(d.ship) : up5(d.shipUsd || 45));
  const moneyOf = (d) => (c) => formatMoney(c, d.moneyFormat, false);

  /* ---------- <mm-cart-nudge> "Add another" (snippets/mm-cart-line) ----------
     On the first plain line of a product with a Maison engine tier, while its bags in the cart (n) are below the top
     tier: one more costs engine total(n + 1) − engine total(n) (MaisonEngine.tierLocalCents, cart currency; below the
     first tier the engine charges singles) and saves that bag's own price minus that. The setting nudge_ship (default
     "plus free shipping") follows after a comma, only when that one more takes the cart from under the free-shipping
     minimum to it ("…and save $9.95, plus free shipping"). Shown only while the engine prices this cart
     (config.live, or the cart carries _mm_engine=1), so the line never quotes a price the cart won't charge. Its button
     is the line's own "+" ([data-mm-change], cart/change.js quantity + 1), handled by bindLines like every line button. */
  class MmCartNudge extends HTMLElement {
    connectedCallback() {
      const E = window.MaisonEngine;
      const d = this.dataset;
      const cfg = E?.config?.();
      const tier = cfg?.tiers?.[d.productId];
      const out = this.querySelector('[data-nudge-text]');
      if (!out || !tier?.totals || !(cfg.live === true || d.engine === '1')) return;
      const qs = Object.keys(tier.totals).map(Number).filter((q) => q > 1).sort((a, b) => a - b);
      const n = Number(d.qty);
      if (!qs.length || !(n >= 1) || n >= qs[qs.length - 1]) return;
      const unit = Number(d.unit);
      const singles = Number(d.singles);
      const total = (q, cents) => (q >= qs[0] ? E.tierLocalCents(tier, q, cents) : cents);
      const now = total(n, singles);
      const next = total(n + 1, singles + unit);
      if (now == null || next == null) return;
      const price = Math.round(next - now);
      const save = unit - price;
      if (!(price > 0) || !(save > 0)) return;
      const ship = shipOf(d);
      const progress = Number(d.total || 0);
      const crosses = ship > 0 && progress < ship && progress + price >= ship;
      const money = moneyOf(d);
      const html = esc(d.tText || '').replace(/\{(price|amount)\}/g, (_, k) => `<strong>${esc(money(k === 'price' ? price : save))}</strong>`);
      out.innerHTML = crosses && d.tShip ? `${html}, ${esc(d.tShip)}` : html;
      this.hidden = false;
    }
  }

  /* ---------- <mm-cart-upsells> (snippets/mm-cart-line part 'upsells') ----------
     Liquid already leads with the cheapest suggestion that closes the free-shipping gap wherever it knows the minimum
     (data-ship). For currencies converted here: same rule, moved first with CSS order (moving the node would run the
     product card's setup again) and its flag shown. */
  class MmCartUpsells extends HTMLElement {
    connectedCallback() {
      const d = this.dataset;
      if (d.ship) return;
      const gap = shipOf(d) - Number(d.total || 0);
      if (!(gap > 0)) return;
      let lead = null;
      this.querySelectorAll('.mm-cd__upsell[data-price]').forEach((li) => {
        const p = Number(li.dataset.price);
        if (p >= gap && (!lead || p < Number(lead.dataset.price))) lead = li;
      });
      const flag = lead?.querySelector('[data-flag]');
      if (!flag) return;
      lead.style.order = '-1';
      flag.hidden = false;
    }
  }

  /* ---------- <mm-cart-goal> the empty drawer's threshold line (snippets/mm-cart-line part 'goal') ----------
     Liquid writes it when it knows the minimum; otherwise it is converted here, formatted like the bar's label. */
  class MmCartGoal extends HTMLElement {
    connectedCallback() {
      const d = this.dataset;
      const out = this.querySelector('[data-goal-text]');
      if (d.ship || !out) return;
      const s = formatMoney(shipOf(d), d.moneyFormat, false);
      out.textContent = String(d.tLabel || '').replace('[amount]', s.includes('$') ? `${s} ${d.currency}` : s);
    }
  }

  define('mm-cart-rewards', MmCartRewards);
  define('mm-cart-nudge', MmCartNudge);
  define('mm-cart-upsells', MmCartUpsells);
  define('mm-cart-goal', MmCartGoal);
  define('mm-cart-drawer', MmCartDrawer);
  define('mm-cart-page', MmCartPage);

  // Header cart icons (any page): open the drawer instead of following the /cart link, unless the cart type is Page.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest?.(TRIGGERS);
    if (!trigger || cartType() === 'page') return;
    const drawer = document.querySelector('mm-cart-drawer');
    if (!drawer) return;
    e.preventDefault();
    drawer.open(trigger);
  });

  window.MMCart = {
    open: () => document.querySelector('mm-cart-drawer')?.open(),
    close: () => document.querySelector('mm-cart-drawer')?.close(),
  };
})();
